/**
 * useSnippetDraft - Hook for auto-saving snippet draft data
 *
 * Provides debounced auto-save functionality for the snippet wizard.
 * Saves draft data to the backend every 3 seconds when changes are detected.
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { SnippetDraftData, updateSnippetDraft } from "../api/snippets";

interface UseSnippetDraftResult {
  isSaving: boolean;
  lastSaved: Date | null;
  saveError: Error | null;
  saveNow: () => Promise<void>;
}

const DEBOUNCE_MS = 3000; // 3 seconds

export function useSnippetDraft(
  snippetId: number,
  draftData: SnippetDraftData
): UseSnippetDraftResult {
  const { getAccessTokenSilently } = useAuth0();
  
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [saveError, setSaveError] = useState<Error | null>(null);

  // Track the last saved data to avoid unnecessary saves
  const lastSavedDataRef = useRef<string>("");
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef(true);

  // Save function
  const performSave = useCallback(async () => {
    const currentData = JSON.stringify(draftData);
    
    // Skip if data hasn't changed
    if (currentData === lastSavedDataRef.current) {
      return;
    }

    setIsSaving(true);
    setSaveError(null);

    try {
      const token = await getAccessTokenSilently();
      await updateSnippetDraft(snippetId, draftData, token);
      
      if (isMountedRef.current) {
        lastSavedDataRef.current = currentData;
        setLastSaved(new Date());
      }
    } catch (error) {
      if (isMountedRef.current) {
        setSaveError(error instanceof Error ? error : new Error("Save failed"));
      }
    } finally {
      if (isMountedRef.current) {
        setIsSaving(false);
      }
    }
  }, [snippetId, draftData, getAccessTokenSilently]);

  // Debounced save effect
  useEffect(() => {
    // Clear any existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Set up new debounced save
    saveTimeoutRef.current = setTimeout(() => {
      performSave();
    }, DEBOUNCE_MS);

    // Cleanup
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [draftData, performSave]);

  // Track mounted state
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Save on unmount (window close, navigation, etc.)
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Attempt synchronous save - browser may or may not complete it
      const currentData = JSON.stringify(draftData);
      if (currentData !== lastSavedDataRef.current) {
        // Use sendBeacon for more reliable unload saves
        // Note: sendBeacon doesn't support custom headers, so this is a best-effort save
        if (navigator.sendBeacon) {
          const body = JSON.stringify({ draft_data: draftData });
          navigator.sendBeacon(
            `${import.meta.env.VITE_API_URL || "http://localhost:8000/api"}/snippets/${snippetId}/draft`,
            new Blob([body], { type: "application/json" })
          );
        }
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [snippetId, draftData]);

  // Manual save function for immediate saving
  const saveNow = useCallback(async () => {
    // Clear pending debounced save
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }
    await performSave();
  }, [performSave]);

  return {
    isSaving,
    lastSaved,
    saveError,
    saveNow,
  };
}

export default useSnippetDraft;
